import { RecoverableBinding, recoverableField } from "../../../ServiceDesk/recovery/recoverable-control";
import { LoggingService } from "../../../ServiceDesk/sdServices/loggingSvc";
import { UiControlConstants } from "../../common/uicontrol-constants";
import { CustomKeyValues } from "../../logger/telemetry-service";
import { SelectMenuItem } from "../selectmenu/selectmenu";

/** ###TypeDocName:sd-pivot### */
export class PivotBinding {
  /** True if the pivot should be disabled, false otherwise. */
  public isDisabled = "@";

  /** The name of the pivot, the text which is shown in the pivot header.*/
  public name = "@";

  /** Callback to support eventing on pivot selection. There are no event arguments for this callback. */
  public onSelected = "&?";

  /** The identifier for all business intelligence (BI) actions triggered by this component.*/
  public biId = "@?";

  /** The identifier used for automation purposes. */
  public automationId = "@?";
  /** True if the pivot can be closed, false otherwise. */
  public isCloseable = "<?";

  /**scenario name for awa telemetry logging */
  public scenario = "<?";

  /**telemetry data for awa telemetry logging */
  public chosePivotData = "<?";

  /** True if the pivot icon can be visible, false otherwise. */
  public isIconVisible = "<?";

  /** The icon of the pivot, which is shown in the pivot header.*/
  public iconClass = "@";

  /** pivot icon label for accessibility */
  public iconLabel = "@";

  /** pivot icon title */
  public iconTooltip = "@";
}

// TODO : Need multiple binding discovery support in doc pages.
/** ###TypeDocName:sd-pivots### */
export class PivotsBinding extends RecoverableBinding {
  /** True if we want to select pivots automatically as they are added to the control, false otherwise. The default value is false. */
  public selectNewPivots = "@?";

  /** Callback used to mine an identifier on an opaque object. Arguments are the matching scope for individual pivots (in the case of ng-repeat, your identifier name for the repeated object will be the argument you want to use).*/
  public pivotIdentifierGetter = "&?";

  /** The currently selected pivot identifier. If set on the other side of the two way binding, it will update the selected pivot of this control. */
  public activePivotIdentifier = "=?";

  /** True if the pivots control should arrange pivots vertically, false otherwise. The default value is false.  This is a one-time binding */
  public isVertical = "@?";

  /** Callback for pivot selection. The callback for this is { pivot: PivotControl | any }. If pivot pivot identifier getters are used, the bound object will be passed back as the argument instead of a PivotControl. */
  public pivotSelected = "&?";

  /** Callback to support eventing on pivot removal. The callback for this is { pivot: PivotControl | any }. */
  public pivotRemoved = "&";

  /**scenario name for awa telemetry logging */
  public scenario = "<?";

  /**telemetry data for awa telemetry logging */
  public chosePivotData = "<?";
}

export type PivotWidthModel = { name: string; width: number };
export class PivotsControl {
  public idNamespace: string;
  public activePivot: PivotControl;
  public pivots: PivotControl[] = [];
  public pivotsLength: number = 0;
  public overflownPivotsMenuItems: SelectMenuItem[] = []; // contains the overflowing pivots
  public pivotSelected: (args: { pivot: PivotControl | any }) => void;
  public pivotRemoved: (args: { pivot: PivotControl | any; event: ng.IAngularEvent }) => void;
  public showsContent = false;
  public selectNewPivots: boolean;
  public scenario: string;
  public chosePivotStep: string;
  @recoverableField
  public activePivotIdentifier: any;
  public pivotIdentifierGetter: (arg: { pivotScope: ng.IScope }) => any;
  public isVertical: boolean;
  public arePivotsOverflown: boolean;
  public pivotsOverflownIndex = 1;
  public initialPivotWidth = 40;
  public totalPivotWidth = this.initialPivotWidth; // width of select menu overflowing container
  public isIconVisible = false; // icon do not visible by default
  private availableHeaderWidth: number;
  private pivotHeaderWidths: PivotWidthModel[] = [];
  private isElementVisible = false;
  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private $element: ng.IAugmentedJQuery,
    private uiControlConstants: UiControlConstants
  ) {

    this.pivotsLength = this.pivots.length;

    if (this.scenario) {
      this.chosePivotStep = this.scenario + ".ChosePivot";
    }
    this.$scope.$watch(
      () => {
        return this.activePivotIdentifier;
      },
      (newValue) => {
        if (
          newValue &&
          this.pivotIdentifierGetter &&
          (!this.activePivot ||
            (this.activePivot &&
              newValue !== this.pivotIdentifierGetter({ pivotScope: this.activePivot.$scope.$parent })))
        ) {
          this.pivots.forEach((pivot) => {
            if (this.pivotIdentifierGetter({ pivotScope: pivot.$scope.$parent }) == newValue) {
              this.selectPivot(pivot);
              return;
            }
          });
        }
      }
    );

    let resizeListener = () => {
      this.$scope.$evalAsync(() => {
        this.processPivots();
      });
    };
    window.addEventListener("resize", resizeListener);
    this.$scope.$on("destroy", () => {
      window.removeEventListener("resize", resizeListener);
    });

    // Track when this element is visible in DOM terms, if it becomes visible then we process pivots. This addresses a problem where pivots were getting hidden when they were invisible
    this.$scope.$watch(
      () => {
        return this.$element.is(":visible");
      },
      (newValue, oldValue) => {
        this.isElementVisible = newValue;
        if (newValue) {
          this.processPivots();
        }
      }
    );
  }

  public getAvailableHeaderWidth(): number {
    if (this.availableHeaderWidth) {
      return this.availableHeaderWidth;
    }

    return this.$element.find(".sd-pivot header").outerWidth();
  }

  public getPivotHeaderWidths(): PivotWidthModel[] {
    let pivotHeaderWidths = this.getPivotWidths();
    pivotHeaderWidths.forEach((pivotHeader: PivotWidthModel) => {
      let index = this.pivotHeaderWidths.findIndex((value: PivotWidthModel) => {
        return value.name == pivotHeader.name;
      });

      if (index < 0) {
        this.pivotHeaderWidths.push(pivotHeader);
      }
    });

    return this.pivotHeaderWidths;
  }

  public getPivotWidths() {
    let pivotHeaderWidths: PivotWidthModel[] = [];
    let pivotElements = this.$element.find("header a.sd-pivot-item");
    pivotElements.each((index: number) => {
      if (this.pivots[index]) {
        let pivotElement = pivotElements[index];
        let pivotHeaderWidth = (<HTMLElement>pivotElement).offsetWidth;
        let style = window.getComputedStyle(pivotElement);
        pivotHeaderWidth = pivotHeaderWidth + (parseFloat(style.marginLeft) ? parseFloat(style.marginLeft) : 0);
        pivotHeaderWidth = pivotHeaderWidth + (parseFloat(style.marginRight) ? parseFloat(style.marginRight) : 0);
        pivotHeaderWidths.push({ name: this.pivots[index].name, width: pivotHeaderWidth });
      }
    });

    return pivotHeaderWidths;
  }

  public selectPivot(pivot: PivotControl, $event?: ng.IAngularEvent): void {
    // Note: This method is meant to be used from a button click, but instead the wrapped method
    //  "setActivePivot" will be used for pivot changes coming from outside this control.
    if ($event) {
      $event.preventDefault();
    }

    this.setActivePivot(pivot);
    if (this.pivotSelected) {
      this.pivotSelected({ pivot: pivot });
    }

    if (pivot.onSelected) {
      pivot.onSelected();
    }
  }

  public selectPivotonEnter(pivot: PivotControl, $event?: any): void {
    if ($event.keyCode == this.uiControlConstants.keyCode.enterKeyCode) {
      this.setActivePivot(pivot);
    }
  }

  public pivotDisabled(pivot: PivotControl) {
    return pivot.isDisabled;
  }

  public menuItemSelected(menuItem: SelectMenuItem) {
    this.selectPivot(<PivotControl>menuItem.itemValue);
    let pivotsTemp = this.pivots.filter((p) => p != menuItem.itemValue);
    let totalPivotWidth = this.initialPivotWidth;
    let insertIndex = 0;
    let selectedPivotWidth = this.getPivotHeaderWidth(menuItem.itemValue);
    for (insertIndex; insertIndex < pivotsTemp.length; insertIndex++) {
      totalPivotWidth = totalPivotWidth + this.getPivotHeaderWidth(pivotsTemp[insertIndex]);
      if (totalPivotWidth + selectedPivotWidth > this.getAvailableHeaderWidth()) {
        break;
      }
    }
    pivotsTemp.splice(insertIndex, 0, menuItem.itemValue);
    this.pivots = pivotsTemp;
    this.processPivots();
  }

  public getPivotHeaderWidth(pivot: PivotControl): number {
    let index = this.getPivotHeaderWidths().findIndex((pivotModel: PivotWidthModel) => {
      return pivotModel.name == pivot.name;
    });

    if (this.pivotHeaderWidths[index]) {
      return this.pivotHeaderWidths[index].width;
    }
  }

  public processPivots() {
    if (!this.isVertical && this.isElementVisible) {
      let totalPivotWidth = this.initialPivotWidth;
      this.overflownPivotsMenuItems = [];
      // get the overflowing pivots
      setTimeout(function (): void {
        if (this.pivot) {
          this.pivots.forEach((pivot: PivotControl) => {
            totalPivotWidth += this.getPivotHeaderWidth(pivot);
            if (totalPivotWidth > this.getAvailableHeaderWidth()) {
              pivot.isHidden = true;
              this.overflownPivotsMenuItems.push({ itemName: pivot.name, itemValue: pivot });
            } else {
              pivot.isHidden = false;
            }
          });
        }
      }, 10);
    }
  }

  public addPivot(pivot: PivotControl) {
    pivot.sortIndex = this.pivots.length
      ? Math.max.apply(
          undefined,
          this.pivots.map((p: PivotControl) => {
            return p.sortIndex;
          })
        ) + 1
      : 0;
    this.pivots.push(pivot);

    // Two main cases here : If we aren't using pivot identifiers (i.e. bound selection) then we should select the first item by default,
    //  and obey the selectNewPivots value.
    if (
      (!this.pivotIdentifierGetter && (this.pivots.length === 1 || this.selectNewPivots)) ||
      // Second main case: pivotIdentifierGetter is present, and we see that it matches the active value (i.e. active value was
      //    set before pivots were added). The pivot should be selected.
      (this.pivotIdentifierGetter &&
        this.pivotIdentifierGetter({ pivotScope: pivot.$scope.$parent }) == this.activePivotIdentifier)
    ) {
      this.selectPivot(pivot);
    }
  }

  public removePivot(pivot: PivotControl, $event?: ng.IAngularEvent) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.overflownPivotsMenuItems = this.overflownPivotsMenuItems.filter(
      (existingPivot) => existingPivot.itemValue != pivot
    );
    let pivotToRemoveIndex = this.pivots.indexOf(pivot);
    if (pivotToRemoveIndex >= 0) {
      this.pivots.splice(pivotToRemoveIndex, 1);
    }

    if (this.activePivot == pivot) {
      this.setActivePivot(undefined);
    }
    this.pivotRemoved({ pivot: pivot, event: $event });
    this.processPivots();
  }

  private setActivePivot(pivot: PivotControl): void {
    if (pivot === this.activePivot) {
      return;
    }

    if (this.activePivot) {
      this.activePivot.isActive = false;
    }

    if (!pivot) {
      this.activePivot = undefined;
      return;
    }

    this.activePivot = pivot;
    pivot.isActive = true;

    if (this.pivotIdentifierGetter) {
      // The scope being sent into pivotScope here needs to match the scope of whatever is repeated on sd-pivot.
      this.activePivotIdentifier = this.pivotIdentifierGetter({ pivotScope: pivot.$scope.$parent });
    }
  }
}
export class PivotControl {
  pivotsCtrl: PivotsControl;
  onSelected: () => void;
  name: string;
  isCloseable: boolean;
  isDisabled: boolean;
  isActive: boolean;
  sortIndex: number;
  headingElement: Element | NodeList;
  biId: string;
  automationId: string;
  isHidden = false;
  scenario: string;
  chosePivotStep: string;
  chosePivotData: CustomKeyValues;
  iconClass: string;
  isIconVisible: boolean;
  iconLabel: string;
  iconTooltip: string;
  // @ngInject
  constructor(public $scope: ng.IScope, private loggingService: LoggingService) {
    if (this.scenario) {
      this.chosePivotStep = this.scenario + ".ChosePivot";
    }
    $scope.$on("$destroy", () => {
      if (this.pivotsCtrl != undefined) {
        this.pivotsCtrl.removePivot(this);
      }
    });
  }

  public $postLink(): void {
    if (!this.pivotsCtrl) {
      // Todo move to logger
      this.loggingService.logError(
        "The pivot directive needs to set pivotsCtrl variable due to strange lifecycle issues."
      );
    }

    this.pivotsCtrl.addPivot(this);
  }

  public getPivotDirection(): boolean {
    // case of no active pivot should be extremely rare. Default return value is arbitrary
    if (this.pivotsCtrl.activePivot) {
      return this.pivotsCtrl.pivots.indexOf(this.pivotsCtrl.activePivot) < this.sortIndex;
    }
    return true;
  }
}

export const sdPivots: ng.IComponentOptions = {
  controller: PivotsControl,
  templateUrl: "../ShellCommon/Controls/pivots/pivots.html",
  transclude: true,
  bindings: <any>new PivotsBinding()
};
export function sdPivot(): ng.IDirective {
  return {
    transclude: true,
    replace: true,
    bindToController: <any>new PivotBinding(),
    controllerAs: "$ctrl",
    require: ["^^sdPivots", "sdPivot"],
    scope: {},
    controller: PivotControl,
    link: (
      scope: ng.IScope,
      element: ng.IRootElementService,
      attr: ng.IAttributes,
      pivots: { "0": PivotsControl; "1": PivotControl },
      $transclude: ng.ITranscludeFunction
    ) => {
      pivots["1"].pivotsCtrl = pivots["0"];

      $transclude(function (clone: JQuery) {
        let elementsToUse: Element[] = [];
        angular.forEach(clone, (node: Element, key: number) => {
          if (node.tagName && node.tagName.toLowerCase() === "sd-pivot-header") {
            pivots["1"].headingElement = node.childNodes;
          }
          elementsToUse.push(node);
        });

        let targetElement = element[0].querySelector("pivot-transclusion-target");
        $(targetElement).replaceWith(elementsToUse);
      });
    },
    templateUrl: "../ShellCommon/Controls/pivots/pivot.html"
  };
}
export function sdPivotHeadingTransclude(): ng.IDirective {
  return {
    link: (scope: ng.IScope, element: ng.IRootElementService, attr: ng.IAttributes, pivotCtrl: PivotControl) => {
      // This guy should be taking the element out of a well known location on pivot, and replacing the target with it.
      let pivot = scope.$eval(attr["sdPivotHeadingTransclude"]);
      scope.$applyAsync(() => {
        if (pivot.headingElement) {
          element.replaceWith(pivot.headingElement);
        }
      });
    }
  };
}
